import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * OPTIMIZATION: Memoized dewar card component
 * Only re-renders when dewar prop changes
 * Reduces unnecessary re-renders in lists
 */
const OptimizedDewarCard = memo(({ dewar }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/dewars/${dewar?.id}`);
  };

  // OPTIMIZATION: Calculate totals only when needed
  const totalBoxes = dewar?.racks?.reduce((total, rack) => total + (rack?.boxes?.length || 0), 0) || 0;
  const totalSamples = dewar?.racks?.reduce(
    (total, rack) => 
      total + (rack?.boxes?.reduce((boxTotal, box) => boxTotal + (box?.occupied || 0), 0) || 0),
    0
  ) || 0;

  return (
    <div 
      onClick={handleClick}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{dewar?.name}</h3>
          <p className="text-sm text-gray-600">{dewar?.location}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-600">Temperature</p>
          <p className="text-sm font-medium text-gray-900">{dewar?.temp}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Status</p>
          <p className={`text-sm font-medium ${
            dewar?.status === 'Operational' ? 'text-green-600' :
            dewar?.status === 'Warning'? 'text-yellow-600' : 'text-red-600'
          }`}>
            {dewar?.status}
          </p>
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100 space-y-3">
        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="text-gray-600">Boxes: </span>
            <span className="font-medium text-gray-900">{totalBoxes}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">Samples: </span>
            <span className="font-medium text-gray-900">{totalSamples}</span>
          </div>
        </div>

        {/* Horizontal LN2 Level Gauge */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-600">N₂ Level:</span>
            <span className={`text-sm font-bold ${
              (dewar?.liquidNitrogenLevel || 14) <= 4
                ? 'text-red-600'
                : (dewar?.liquidNitrogenLevel || 14) <= 8
                ? 'text-yellow-600'
                : 'text-green-600'
            }`}>
              {dewar?.liquidNitrogenLevel || 14}" / 22"
            </span>
          </div>
          <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                (dewar?.liquidNitrogenLevel || 14) <= 4
                  ? 'bg-red-500'
                  : (dewar?.liquidNitrogenLevel || 14) <= 8
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{
                width: `${Math.min(100, ((dewar?.liquidNitrogenLevel || 14) / 22) * 100)}%`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // OPTIMIZATION: Custom comparison function
  // Only re-render if dewar ID or key properties change
  return (
    prevProps?.dewar?.id === nextProps?.dewar?.id &&
    prevProps?.dewar?.status === nextProps?.dewar?.status &&
    prevProps?.dewar?.liquidNitrogenLevel === nextProps?.dewar?.liquidNitrogenLevel &&
    prevProps?.dewar?.racks?.length === nextProps?.dewar?.racks?.length
  );
});

OptimizedDewarCard.displayName = 'OptimizedDewarCard';

export default OptimizedDewarCard;